<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Dashboard Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>
      <p class="mt-2 text-sm text-gray-700">
        Welcome back! Here's an overview of your profile, job posts, and saved jobs.
      </p>
    </div>

    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.489M21 13.255A23.931 23.931 0 0012 15c3.183 0 6.22-.815 8.964-2.489M21 13.255A23.931 23.931 0 0112 15c3.183 0-6.22-.815-8.964-2.489" />
              </svg>
            </div>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Job Posts</p>
            <p class="text-2xl font-semibold text-gray-900">{{ stats.jobPosts }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
              <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </div>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Saved Jobs</p>
            <p class="text-2xl font-semibold text-gray-900">{{ stats.savedJobs }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Profile Section -->
      <div class="lg:col-span-1">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">Profile</h2>
            <button
              @click="editProfile"
              class="text-sm text-red-900 hover:text-red-800 font-medium"
            >
              Edit
            </button>
          </div>

          <div class="text-center mb-4">
            <div class="w-20 h-20 bg-gray-300 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span class="text-2xl font-bold text-gray-700">{{ userProfile.initials }}</span>
            </div>
            <h3 class="text-lg font-semibold text-gray-900">{{ userProfile.fullName }}</h3>
            <p class="text-gray-600">{{ userProfile.title }}</p>
            <p class="text-sm text-gray-500">{{ userProfile.location }}</p>
          </div>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Profile Completion</span>
              <span class="text-sm font-medium text-gray-900">{{ userProfile.completion }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-red-900 h-2 rounded-full"
                :style="{ width: userProfile.completion + '%' }"
              ></div>
            </div>
          </div>

          <div class="mt-4 space-y-2">
            <div class="flex items-center text-sm text-gray-600">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
              {{ userProfile.email }}
            </div>
            <div class="flex items-center text-sm text-gray-600">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ userProfile.location }}
            </div>
          </div>
        </div>
      </div>

      <!-- Job Posts Section -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">My Job Posts</h2>
            <a
              href="/post-job"
              class="bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Post New Job
            </a>
          </div>

          <div v-if="jobPosts.length === 0" class="text-center py-8 text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.489M21 13.255A23.931 23.931 0 0012 15c3.183 0 6.22-.815 8.964-2.489M21 13.255A23.931 23.931 0 0112 15c3.183 0-6.22-.815-8.964-2.489" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No job posts yet</h3>
            <p class="mt-1 text-sm text-gray-500">Start sharing job opportunities with the community!</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="job in jobPosts.slice(0, 3)"
              :key="job.id"
              class="border border-gray-200 rounded-lg p-4 hover:border-red-300 cursor-pointer"
              @click="viewJobPost(job.id)"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h3 class="font-semibold text-gray-900">{{ job.title }}</h3>
                  <p class="text-gray-600">{{ job.company }}</p>
                  <p class="text-sm text-gray-500 mt-1">{{ job.description.substring(0, 100) }}...</p>
                </div>
                <div class="text-right ml-4">
                  <span class="text-sm text-gray-500">{{ formatDate(job.postedDate) }}</span>
                  <div class="mt-2 flex space-x-2">
                    <button
                      @click.stop="editJobPost(job.id)"
                      class="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      @click.stop="deleteJobPost(job.id)"
                      class="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="jobPosts.length > 3" class="text-center pt-4">
              <a
                href="/admin/job-posts"
                class="text-red-900 hover:text-red-800 font-medium"
              >
                View All Job Posts →
              </a>
            </div>
          </div>
        </div>

        <!-- Saved Jobs Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">Saved Jobs</h2>
            <a
              href="/admin/bookmarks"
              class="text-red-900 hover:text-red-800 font-medium"
            >
              View All
            </a>
          </div>

          <div v-if="savedJobs.length === 0" class="text-center py-8 text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No saved jobs yet</h3>
            <p class="mt-1 text-sm text-gray-500">Save interesting jobs to view them here!</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="job in savedJobs.slice(0, 3)"
              :key="job.id"
              class="border border-gray-200 rounded-lg p-4 hover:border-red-300 cursor-pointer"
              @click="viewJob(job.id)"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h3 class="font-semibold text-gray-900">{{ job.title }}</h3>
                  <p class="text-gray-600">{{ job.company }}</p>
                  <p class="text-sm text-gray-500 mt-1">{{ job.location }}</p>
                </div>
                <div class="text-right ml-4">
                  <span class="text-sm text-gray-500">{{ formatDate(job.savedDate) }}</span>
                  <button
                    @click.stop="removeSavedJob(job.id)"
                    class="mt-2 text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <div v-if="savedJobs.length > 3" class="text-center pt-4">
              <a
                href="/admin/bookmarks"
                class="text-red-900 hover:text-red-800 font-medium"
              >
                View All Saved Jobs →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Data
const userProfile = ref({})
const stats = ref({})
const jobPosts = ref([])
const savedJobs = ref([])

// Sample data (replace with API calls)
const sampleProfile = {
  fullName: 'John Doe',
  title: 'Senior Software Engineer',
  email: 'john.doe@email.com',
  location: 'San Francisco, CA',
  initials: 'JD',
  completion: 85
}

const sampleStats = {
  profileViews: 45,
  jobPosts: 3,
  savedJobs: 8
}

const sampleJobPosts = [
  {
    id: 1,
    title: 'Senior Software Engineer',
    company: 'TechCorp International',
    description: 'Looking for a senior developer to join our Berlin team. Remote work possible, competitive salary, great benefits.',
    postedDate: '2024-01-15'
  },
  {
    id: 2,
    title: 'Marketing Manager',
    company: 'Global Marketing Solutions',
    description: 'Join our Amsterdam office as Marketing Manager. Lead international campaigns and manage a team of 5.',
    postedDate: '2024-01-14'
  },
  {
    id: 3,
    title: 'UX Designer',
    company: 'Creative Studios',
    description: 'Remote UX Designer position for international projects. Flexible hours and competitive hourly rate.',
    postedDate: '2024-01-13'
  }
]

const sampleSavedJobs = [
  {
    id: 1,
    title: 'Frontend Developer',
    company: 'Digital Solutions',
    location: 'Munich, Germany',
    savedDate: '2024-01-16'
  },
  {
    id: 2,
    title: 'Full Stack Engineer',
    company: 'Innovation Labs',
    location: 'Hamburg, Germany',
    savedDate: '2024-01-15'
  },
  {
    id: 3,
    title: 'Product Manager',
    company: 'Tech Startup',
    location: 'Berlin, Germany',
    savedDate: '2024-01-14'
  }
]

// Methods
const editProfile = () => {
  router.push('/admin/profile/edit')
}

const viewJobPost = (jobId) => {
  router.push(`/admin/job-posts/${jobId}`)
}

const editJobPost = (jobId) => {
  router.push(`/admin/job-posts/${jobId}/edit`)
}

const deleteJobPost = (jobId) => {
  if (confirm('Are you sure you want to delete this job post?')) {
    // In a real app, this would make an API call to delete the job post
    jobPosts.value = jobPosts.value.filter(job => job.id !== jobId)
    stats.value.jobPosts--
  }
}

const viewJob = (jobId) => {
  router.push(`/jobs/${jobId}`)
}

const removeSavedJob = (jobId) => {
  if (confirm('Remove this job from saved jobs?')) {
    // In a real app, this would make an API call to remove the saved job
    savedJobs.value = savedJobs.value.filter(job => job.id !== jobId)
    stats.value.savedJobs--
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  // In a real app, this would fetch data from API
  userProfile.value = sampleProfile
  stats.value = sampleStats
  jobPosts.value = sampleJobPosts
  savedJobs.value = sampleSavedJobs
})
</script>
